/**
 * Contact Entity - Log of interactions with applicants
 * 
 * Tracks phone calls, emails, in-person meetings, and SMS messages.
 */
import { z } from 'zod';
import { ContactId, ApplicationId, UserId } from '../types/branded';
import { ContactType, ContactDirection } from '../types/enums';

// ============================================================================
// Contact Entity
// ============================================================================

export const ContactSchema = z.object({
  id: ContactId,
  applicationId: ApplicationId,
  
  // Contact details
  type: ContactType,
  direction: ContactDirection,
  summary: z.string().min(1).max(2000),
  
  // Who made the contact
  contactedBy: UserId,
  contactedAt: z.number().int(), // Unix seconds
  
  // Follow-up tracking
  followUpRequired: z.boolean().default(false),
  followUpDate: z.number().int().nullable().optional(), // Unix seconds
  followUpCompleted: z.boolean().default(false),
  
  // Timestamps
  createdAt: z.number().int(),
  updatedAt: z.number().int(),
});
export type Contact = z.infer<typeof ContactSchema>;

// ============================================================================
// Input Types
// ============================================================================

export const CreateContactInputSchema = z.object({
  applicationId: ApplicationId,
  type: ContactType,
  direction: ContactDirection,
  summary: z.string().min(1).max(2000),
  contactedAt: z.number().int().optional(), // Defaults to now
  followUpRequired: z.boolean().optional(),
  followUpDate: z.number().int().nullable().optional(),
});
export type CreateContactInput = z.infer<typeof CreateContactInputSchema>;

export const UpdateContactInputSchema = z.object({
  contactId: ContactId,
  summary: z.string().min(1).max(2000).optional(),
  followUpRequired: z.boolean().optional(),
  followUpDate: z.number().int().nullable().optional(),
  followUpCompleted: z.boolean().optional(),
});
export type UpdateContactInput = z.infer<typeof UpdateContactInputSchema>;
